import React, { useCallback, useEffect, useState } from "react";
import { apiClient } from "@/lib/apiClient";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Loader2, FileDown, FileArchive, ArrowUpRight, ArrowDownRight, Minus, Pencil, Package } from "lucide-react";
import { AccrualAssistantDialog } from "@/components/AccrualAssistantDialog";

const SECTION_LABELS = {
  assets_current: "Umlaufvermögen",
  assets_fixed: "Anlagevermögen",
  liabilities_short: "Kurzfristiges Fremdkapital",
  liabilities_long: "Langfristiges Fremdkapital",
  equity: "Eigenkapital",
  revenue: "Betriebsertrag",
  material_expense: "Materialaufwand",
  personnel_expense: "Personalaufwand",
  other_operating_expense: "Übriger Betriebsaufwand",
  depreciation: "Abschreibungen",
  financial_result: "Finanzergebnis / Ausserordentliches",
};

const SUBSECTION_LABELS = {
  // Bilanz Sub-Sektionen (OR Art. 959)
  liquid_assets: "Flüssige Mittel & Wertschriften",
  receivables: "Forderungen",
  inventory: "Vorräte",
  accruals_active: "Aktive Rechnungsabgrenzung",
  financial_assets: "Finanzanlagen",
  tangible_assets: "Sachanlagen",
  intangible_assets: "Immaterielle Werte",
  payables_trade: "Verbindlichkeiten aus L&L",
  payables_short_interest: "Kurzfr. verzinsliche Verbindlichkeiten",
  payables_short_other: "Übrige kurzfr. Verbindlichkeiten",
  accruals_passive: "Passive Rechnungsabgrenzung & Rückstellungen",
  payables_long_interest: "Langfr. verzinsliche Verbindlichkeiten",
  payables_long_other: "Übrige langfr. Verbindlichkeiten",
  share_capital: "Aktien-/Stammkapital",
  legal_reserves: "Gesetzliche Reserven",
  retained_earnings: "Gewinnvortrag / Jahresergebnis",
  // ER Sub-Sektionen (OR Art. 959b)
  financial_expense: "Finanzaufwand",
  financial_income: "Finanzertrag",
  non_operating_income: "Betriebsfremder Ertrag",
  non_operating_expense: "Betriebsfremder Aufwand",
  extraordinary_income: "A.o./einmaliger Ertrag",
  extraordinary_expense: "A.o./einmaliger Aufwand",
  income_taxes: "Direkte Steuern",
};

function fmt(value) {
  const n = Number(value || 0);
  return n.toLocaleString("de-CH", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function DeltaCell({ current, previous }) {
  if (previous === null || previous === undefined) return <span>—</span>;
  const delta = (current || 0) - (previous || 0);
  if (Math.abs(delta) < 0.005) {
    return (
      <span className="inline-flex items-center gap-0.5 text-slate-400 text-xs">
        <Minus className="h-3 w-3" /> 0.00
      </span>
    );
  }
  const isUp = delta > 0;
  return (
    <span
      className={`inline-flex items-center gap-0.5 text-xs ${
        isUp ? "text-emerald-700" : "text-rose-700"
      }`}
    >
      {isUp ? <ArrowUpRight className="h-3 w-3" /> : <ArrowDownRight className="h-3 w-3" />}
      {fmt(Math.abs(delta))}
    </span>
  );
}

function SummaryItem({ label, value, previous, compare, currency, testId }) {
  const v = Number(value || 0);
  const cls = v < 0 ? "text-rose-700" : "text-emerald-700";
  return (
    <div data-testid={testId}>
      <dt className="text-[10.5px] uppercase tracking-wide text-slate-500">{label}</dt>
      <dd className={`text-base font-semibold font-mono ${cls}`}>{fmt(v)}</dd>
      {compare && previous !== null && previous !== undefined && (
        <div className="text-[10px] text-slate-500 mt-0.5">
          Vorjahr: <span className="font-mono">{fmt(previous)} {currency}</span>
        </div>
      )}
    </div>
  );
}

function GroupNamesDialog({ companyId, onSaved }) {
  const [open, setOpen] = useState(false);
  const [groups, setGroups] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open || !companyId) return;
    apiClient
      .get(`/companies/${companyId}/account-groups`)
      .then((r) => setGroups((r.data?.groups || []).map((g) => ({ ...g, _dirty: false }))))
      .catch(() => setGroups([]));
  }, [open, companyId]);

  const updateLocal = (idx, value) =>
    setGroups((g) => g.map((x, i) => (i === idx ? { ...x, custom_name: value, _dirty: true } : x)));

  const saveAll = async () => {
    const dirty = groups.filter((g) => g._dirty);
    if (!dirty.length) {
      setOpen(false);
      return;
    }
    setSaving(true);
    try {
      await Promise.all(
        dirty.map((g) =>
          apiClient.put(`/companies/${companyId}/account-groups/${g.prefix}`, {
            name: (g.custom_name || "").trim() || null,
          })
        )
      );
      toast.success("Gruppennamen gespeichert");
      setOpen(false);
      onSaved?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Speichern fehlgeschlagen");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Button
        type="button"
        size="sm"
        variant="outline"
        className="rounded-full"
        onClick={() => setOpen(true)}
        data-testid="fs-group-names-button"
      >
        <Pencil className="h-3.5 w-3.5 mr-1.5" />
        Gruppennamen
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl" data-testid="fs-group-names-dialog">
          <DialogHeader>
            <DialogTitle>Gruppennamen anpassen</DialogTitle>
          </DialogHeader>
          <p className="text-xs text-slate-500 -mt-2">
            Die Untergruppen sind nach Schweizer KMU-Kontenrahmen vorbenannt. Du kannst jeden Namen
            überschreiben (z.B. «340 Dienstleistungserlöse Fitness»). Leeres Feld = Standardname.
          </p>
          <div className="max-h-[55vh] overflow-y-auto pr-1 space-y-1.5">
            {groups.map((g, idx) => (
              <div key={`${g.prefix}-${g.level}`} className="flex items-center gap-3">
                <span
                  className={`font-mono text-xs w-12 shrink-0 ${g.level === 2 ? "font-bold text-slate-800" : "text-slate-500 pl-2"}`}
                >
                  {g.prefix}
                </span>
                <Input
                  value={g.custom_name ?? ""}
                  placeholder={g.default_name || "—"}
                  onChange={(e) => updateLocal(idx, e.target.value)}
                  className="h-8 text-sm"
                  data-testid={`fs-group-name-input-${g.prefix}`}
                />
              </div>
            ))}
            {!groups.length && (
              <p className="text-sm text-slate-500 py-4 text-center">Keine Konten vorhanden.</p>
            )}
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" className="rounded-full" onClick={() => setOpen(false)}>
              Abbrechen
            </Button>
            <Button
              type="button"
              className="rounded-full"
              onClick={saveAll}
              disabled={saving}
              data-testid="fs-group-names-save"
            >
              {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : null}
              Speichern
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function StructuredRows({ rows, compare }) {
  return rows.map((r, i) => {
    const indent = { paddingLeft: `${8 + (r.level - 1) * 16}px` };
    if (r.row_type === "header") {
      return (
        <TableRow key={i} className={r.level === 1 ? "bg-slate-100" : r.level === 2 ? "bg-slate-50" : ""}>
          <TableCell className="font-mono text-xs text-slate-500 w-20">{r.code}</TableCell>
          <TableCell colSpan={compare ? 4 : 2} style={indent}
            className={r.level === 1 ? "font-bold text-sm text-slate-900" : r.level === 2 ? "font-semibold text-xs text-slate-800" : "font-semibold text-[11px] text-slate-600"}>
            {r.label}
          </TableCell>
        </TableRow>
      );
    }
    if (r.row_type === "account") {
      return (
        <TableRow key={i} data-testid={`fs-row-${r.code}`}>
          <TableCell className="font-mono text-xs text-slate-600 w-20">{r.code}</TableCell>
          <TableCell className="text-sm" style={indent}>{r.label || "—"}</TableCell>
          <TableCell className="text-right font-mono text-sm">{fmt(r.amount)}</TableCell>
          {compare && (
            <>
              <TableCell className="text-right font-mono text-xs text-slate-500">
                {r.previous_amount !== null && r.previous_amount !== undefined ? fmt(r.previous_amount) : "—"}
              </TableCell>
              <TableCell className="text-right">
                <DeltaCell current={r.amount} previous={r.previous_amount} />
              </TableCell>
            </>
          )}
        </TableRow>
      );
    }
    const isMilestone = r.row_type === "milestone";
    return (
      <TableRow key={i}
        className={isMilestone ? "bg-slate-900 text-slate-50 font-semibold" : r.level === 1 ? "border-t-2 border-slate-400 font-semibold" : "border-t border-slate-200"}
        data-testid={isMilestone ? `fs-milestone-${i}` : undefined}>
        <TableCell />
        <TableCell style={indent} className={isMilestone ? "text-xs font-bold" : r.level === 1 ? "text-xs font-bold text-slate-800" : "text-xs font-semibold text-slate-600"}>
          {r.label}
        </TableCell>
        <TableCell className="text-right font-mono text-sm font-semibold">{fmt(r.amount)}</TableCell>
        {compare && (
          <>
            <TableCell className={`text-right font-mono text-xs ${isMilestone ? "text-slate-300" : "text-slate-500"}`}>
              {r.previous_amount !== null && r.previous_amount !== undefined ? fmt(r.previous_amount) : "—"}
            </TableCell>
            <TableCell className="text-right">
              {!isMilestone && <DeltaCell current={r.amount} previous={r.previous_amount} />}
            </TableCell>
          </>
        )}
      </TableRow>
    );
  });
}

function SectionRows({ section, lines, compare }) {
  if (!lines || lines.length === 0) return null;
  const sectionTotal = lines.reduce((acc, l) => acc + (l.balance || 0), 0);
  const sectionPrev = compare
    ? lines.reduce((acc, l) => acc + (l.previous_balance || 0), 0)
    : null;

  // Group lines by subsection (preserving array order, since lines arrive sorted by account_id)
  const groups = [];
  for (const line of lines) {
    const sub = line.subsection || "_unspecified";
    const last = groups[groups.length - 1];
    if (last && last.sub === sub) last.lines.push(line);
    else groups.push({ sub, lines: [line] });
  }
  return (
    <>
      <TableRow className="bg-slate-50">
        <TableCell colSpan={compare ? 5 : 3} className="font-semibold text-xs text-slate-700">
          {SECTION_LABELS[section] || section}
        </TableCell>
      </TableRow>
      {groups.map((g, gi) => (
        <React.Fragment key={`${section}-${g.sub}-${gi}`}>
          {g.sub !== "_unspecified" && SUBSECTION_LABELS[g.sub] && (
            <TableRow>
              <TableCell />
              <TableCell colSpan={compare ? 4 : 2} className="text-[11px] uppercase tracking-wide text-slate-500 pt-2 pb-1">
                {SUBSECTION_LABELS[g.sub]}
              </TableCell>
            </TableRow>
          )}
          {g.lines.map((l) => (
            <TableRow key={`${section}-${l.account_id}`} data-testid={`fs-row-${l.account_id}`}>
              <TableCell className="font-mono text-xs text-slate-600 w-20">{l.account_id}</TableCell>
              <TableCell className="text-sm">{l.account_name || "—"}</TableCell>
              <TableCell className="text-right font-mono text-sm">{fmt(l.balance)}</TableCell>
              {compare && (
                <>
                  <TableCell className="text-right font-mono text-xs text-slate-500">
                    {l.previous_balance !== null && l.previous_balance !== undefined
                      ? fmt(l.previous_balance)
                      : "—"}
                  </TableCell>
                  <TableCell className="text-right">
                    <DeltaCell current={l.balance} previous={l.previous_balance} />
                  </TableCell>
                </>
              )}
            </TableRow>
          ))}
        </React.Fragment>
      ))}
      <TableRow className="border-t-2 border-slate-200">
        <TableCell />
        <TableCell className="text-xs font-semibold text-slate-700">
          Total {SECTION_LABELS[section] || section}
        </TableCell>
        <TableCell className="text-right font-mono text-sm font-semibold">
          {fmt(sectionTotal)}
        </TableCell>
        {compare && (
          <>
            <TableCell className="text-right font-mono text-xs text-slate-600">
              {fmt(sectionPrev || 0)}
            </TableCell>
            <TableCell className="text-right">
              <DeltaCell current={sectionTotal} previous={sectionPrev} />
            </TableCell>
          </>
        )}
      </TableRow>
    </>
  );
}

export function FinancialStatementsViewer() {
  const [year, setYear] = useState(new Date().getFullYear().toString());
  const [compare, setCompare] = useState(true);
  const [bs, setBs] = useState(null);
  const [is_, setIs] = useState(null);
  const [notes, setNotes] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [bundleLoading, setBundleLoading] = useState(false);
  const [handoverLoading, setHandoverLoading] = useState(false);
  const [lockedUntil, setLockedUntil] = useState(null);

  const companyId = localStorage.getItem("demoCompanyId");

  useEffect(() => {
    if (!companyId) return;
    apiClient.get(`/companies/${companyId}/period-lock`)
      .then((r) => setLockedUntil(r.data?.locked_until || null))
      .catch(() => {});
  }, [companyId]);

  const yearLocked = lockedUntil && `${year}-12-31` <= lockedUntil;

  const load = useCallback(async () => {
    if (!companyId || !year) return;
    setLoading(true);
    setError("");
    try {
      const params = { year: parseInt(year, 10), compare_previous: compare };
      const [bsRes, isRes, notesRes] = await Promise.all([
        apiClient.get(`/companies/${companyId}/balance-sheet`, { params }),
        apiClient.get(`/companies/${companyId}/income-statement`, { params }),
        apiClient.get(`/companies/${companyId}/notes`, { params: { year: parseInt(year, 10) } }),
      ]);
      setBs(bsRes.data);
      setIs(isRes.data);
      setNotes(notesRes.data);
    } catch (e) {
      setError(e.response?.data?.detail || "Fehler beim Laden.");
    } finally {
      setLoading(false);
    }
  }, [companyId, year, compare]);

  useEffect(() => {
    load();
  }, [load]);

  const downloadPdf = async (kind) => {
    try {
      const params = kind === "notes"
        ? { year: parseInt(year, 10) }
        : { year: parseInt(year, 10), compare_previous: compare };
      const res = await apiClient.get(
        `/companies/${companyId}/${kind}/pdf`,
        { params, responseType: "blob" },
      );
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const labels = {
        "balance-sheet": "Bilanz",
        "income-statement": "Erfolgsrechnung",
        "notes": "Anhang",
      };
      a.download = `${labels[kind] || kind}_${year}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch {
      // ignore
    }
  };

  const downloadYearEndBundle = async () => {
    if (!companyId) return;
    setBundleLoading(true);
    try {
      const res = await apiClient.get(
        `/companies/${companyId}/year-end-bundle.zip`,
        { params: { year: parseInt(year, 10) }, responseType: "blob" },
      );
      const blob = new Blob([res.data], { type: "application/zip" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Jahresabschluss_Bundle_${year}.zip`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (e) {
      setError(e.response?.data?.detail || "Bundle-Download fehlgeschlagen.");
    } finally {
      setBundleLoading(false);
    }
  };

  const downloadHandoverPackage = async () => {
    if (!companyId) return;
    setHandoverLoading(true);
    try {
      const y = parseInt(year, 10);
      const res = await apiClient.get(
        `/companies/${companyId}/handover-package.zip`,
        { params: { years: `${y - 1},${y}` }, responseType: "blob" },
      );
      const blob = new Blob([res.data], { type: "application/zip" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Abgabe-Paket_${y - 1}_${y}.zip`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toast.success(`Abgabe-Paket ${y - 1}+${y} erstellt`);
    } catch (e) {
      setError(e.response?.data?.detail || "Abgabe-Paket-Download fehlgeschlagen.");
    } finally {
      setHandoverLoading(false);
    }
  };

  const balanceSections = bs && [
    { key: "assets_current", lines: bs.assets_current },
    { key: "assets_fixed", lines: bs.assets_fixed },
    { key: "liabilities_short", lines: bs.liabilities_short },
    { key: "liabilities_long", lines: bs.liabilities_long },
    { key: "equity", lines: bs.equity },
  ];

  const incomeSections = is_ && [
    { key: "revenue", lines: is_.revenue },
    { key: "material_expense", lines: is_.material_expense },
    { key: "personnel_expense", lines: is_.personnel_expense },
    { key: "other_operating_expense", lines: is_.other_operating_expense },
    { key: "depreciation", lines: is_.depreciation },
    { key: "financial_result", lines: is_.financial_result },
  ];

  return (
    <div className="space-y-4" data-testid="financial-statements-viewer">
      <Card className="p-4 border border-slate-200 bg-white">
        <div className="flex flex-wrap items-end gap-4">
          <div className="space-y-1">
            <Label htmlFor="fs-year">Geschäftsjahr</Label>
            <Input
              id="fs-year"
              type="number"
              className="w-28"
              value={year}
              onChange={(e) => setYear(e.target.value)}
              data-testid="fs-year-input"
            />
          </div>
          <label className="inline-flex items-center gap-2 text-sm text-slate-700 mb-2">
            <input
              type="checkbox"
              className="h-4 w-4 rounded border-slate-300"
              checked={compare}
              onChange={(e) => setCompare(e.target.checked)}
              data-testid="fs-compare-toggle"
            />
            Vorjahr vergleichen
          </label>
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="rounded-full"
            disabled={loading}
            onClick={load}
            data-testid="fs-reload-button"
          >
            {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : null}
            Aktualisieren
          </Button>
          <div className="mb-0.5">
            <GroupNamesDialog companyId={companyId} onSaved={load} />
          </div>
          {yearLocked && (
            <span className="mb-1.5 inline-flex items-center gap-1 rounded-full bg-slate-900 text-slate-100 text-xs px-3 py-1" data-testid="fs-locked-badge">
              🔒 Geschäftsjahr {year} definitiv gesperrt
            </span>
          )}
          <div className="ml-auto flex flex-wrap gap-2">
            <AccrualAssistantDialog
              companyId={companyId}
              year={year}
              yearLocked={yearLocked}
              onBooked={load}
            />
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="rounded-full"
              onClick={() => downloadPdf("balance-sheet")}
              data-testid="fs-download-bs-pdf"
            >
              <FileDown className="h-3.5 w-3.5 mr-1.5" /> Bilanz PDF
            </Button>
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="rounded-full"
              onClick={() => downloadPdf("income-statement")}
              data-testid="fs-download-is-pdf"
            >
              <FileDown className="h-3.5 w-3.5 mr-1.5" /> ER PDF
            </Button>
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="rounded-full"
              onClick={() => downloadPdf("notes")}
              data-testid="fs-download-notes-pdf"
            >
              <FileDown className="h-3.5 w-3.5 mr-1.5" /> Anhang PDF
            </Button>
            <Button
              type="button"
              size="sm"
              className="rounded-full bg-emerald-600 text-white hover:bg-emerald-700"
              disabled={bundleLoading}
              onClick={downloadYearEndBundle}
              data-testid="fs-download-year-end-bundle"
            >
              {bundleLoading ? (
                <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
              ) : (
                <FileArchive className="h-3.5 w-3.5 mr-1.5" />
              )}
              {bundleLoading ? "Erstelle Bundle …" : "Year-End-Bundle ZIP"}
            </Button>
            <Button
              type="button"
              size="sm"
              className="rounded-full bg-slate-900 text-white hover:bg-slate-800"
              disabled={handoverLoading}
              onClick={downloadHandoverPackage}
              data-testid="fs-download-handover-package"
              title={`Bilanz, ER, Anhang, Journal & Kontoblätter für ${parseInt(year, 10) - 1} und ${year}`}
            >
              {handoverLoading ? (
                <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
              ) : (
                <Package className="h-3.5 w-3.5 mr-1.5" />
              )}
              {handoverLoading ? "Erstelle Paket …" : `Abgabe-Paket ${parseInt(year, 10) - 1}+${year}`}
            </Button>
          </div>
        </div>
      </Card>

      {error && (
        <div className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-800">
          {error}
        </div>
      )}

      {bs && (
        <Card className="p-5 border border-slate-200 bg-white" data-testid="balance-sheet-card">
          <div className="flex items-baseline justify-between mb-3">
            <div>
              <h3 className="text-base font-semibold text-slate-900">Bilanz</h3>
              <p className="text-xs text-slate-500">
                Per {bs.as_of_date} · Schweizer KMU-Schema (OR Art. 959)
              </p>
            </div>
            <span className="text-xs text-slate-500">{bs.currency}</span>
          </div>

          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-20">Konto</TableHead>
                <TableHead>Bezeichnung</TableHead>
                <TableHead className="text-right">{bs.year}</TableHead>
                {compare && (
                  <>
                    <TableHead className="text-right text-xs text-slate-500">
                      {bs.previous_year || bs.year - 1}
                    </TableHead>
                    <TableHead className="text-right text-xs text-slate-500">Δ</TableHead>
                  </>
                )}
              </TableRow>
            </TableHeader>
            <TableBody>
              {bs.structured_assets?.length ? (
                <>
                  <StructuredRows rows={bs.structured_assets} compare={compare} />
                  <StructuredRows rows={bs.structured_liabilities || []} compare={compare} />
                </>
              ) : (
                balanceSections.map((s) => (
                  <SectionRows key={s.key} section={s.key} lines={s.lines} compare={compare} />
                ))
              )}
              <TableRow className="bg-slate-900 text-slate-50 font-semibold">
                <TableCell colSpan={2}>Total Aktiven</TableCell>
                <TableCell className="text-right font-mono">{fmt(bs.total_assets)}</TableCell>
                {compare && (
                  <>
                    <TableCell className="text-right font-mono text-xs text-slate-300">
                      {fmt(bs.total_assets_previous || 0)}
                    </TableCell>
                    <TableCell className="text-right">
                      <DeltaCell
                        current={bs.total_assets}
                        previous={bs.total_assets_previous}
                      />
                    </TableCell>
                  </>
                )}
              </TableRow>
              <TableRow className="bg-slate-700 text-slate-50">
                <TableCell colSpan={2}>Total Passiven (FK + EK)</TableCell>
                <TableCell className="text-right font-mono">
                  {fmt(bs.total_liabilities_and_equity ?? ((bs.total_liabilities || 0) + (bs.total_equity || 0)))}
                </TableCell>
                {compare && (
                  <>
                    <TableCell className="text-right font-mono text-xs text-slate-300">
                      {fmt((bs.total_liabilities_previous || 0) + (bs.total_equity_previous || 0))}
                    </TableCell>
                    <TableCell />
                  </>
                )}
              </TableRow>
            </TableBody>
          </Table>

          {Math.abs(bs.balance_check_diff || 0) > 0.01 && !bs.structured_liabilities?.some((r) => r.row_type === "milestone") && (
            <div
              className="mt-4 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800"
              data-testid="bs-balance-check-warning"
            >
              <strong>Bilanz nicht ausgeglichen:</strong> Differenz Aktiven − Passiven ={" "}
              <span className="font-mono">{fmt(bs.balance_check_diff)}</span> {bs.currency}.
              Bitte Buchungen prüfen (offene Verbuchungen, Jahresergebnis verschoben?).
            </div>
          )}
        </Card>
      )}

      {is_ && (
        <Card className="p-5 border border-slate-200 bg-white" data-testid="income-statement-card">
          <div className="flex items-baseline justify-between mb-3">
            <div>
              <h3 className="text-base font-semibold text-slate-900">Erfolgsrechnung</h3>
              <p className="text-xs text-slate-500">
                Geschäftsjahr {is_.year} · Schweizer KMU-Schema
              </p>
            </div>
            <span className="text-xs text-slate-500">{is_.currency}</span>
          </div>

          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-20">Konto</TableHead>
                <TableHead>Bezeichnung</TableHead>
                <TableHead className="text-right">{is_.year}</TableHead>
                {compare && (
                  <>
                    <TableHead className="text-right text-xs text-slate-500">
                      {is_.previous_year || is_.year - 1}
                    </TableHead>
                    <TableHead className="text-right text-xs text-slate-500">Δ</TableHead>
                  </>
                )}
              </TableRow>
            </TableHeader>
            <TableBody>
              {is_.structured_rows?.length ? (
                <StructuredRows rows={is_.structured_rows} compare={compare} />
              ) : (
                incomeSections.map((s) => (
                  <SectionRows key={s.key} section={s.key} lines={s.lines} compare={compare} />
                ))
              )}
              <TableRow
                className={`font-semibold ${is_.result >= 0 ? "bg-emerald-600" : "bg-rose-600"} text-white`}
              >
                <TableCell colSpan={2}>
                  Jahresergebnis {is_.result >= 0 ? "(Gewinn)" : "(Verlust)"}
                </TableCell>
                <TableCell className="text-right font-mono">{fmt(is_.result)}</TableCell>
                {compare && (
                  <>
                    <TableCell className="text-right font-mono text-xs">
                      {is_.result_previous !== null && is_.result_previous !== undefined
                        ? fmt(is_.result_previous)
                        : "—"}
                    </TableCell>
                    <TableCell className="text-right">
                      <DeltaCell
                        current={is_.result}
                        previous={is_.result_previous}
                      />
                    </TableCell>
                  </>
                )}
              </TableRow>
            </TableBody>
          </Table>

          <div
            className="mt-5 rounded-lg border border-slate-200 bg-slate-50 p-4"
            data-testid="multi-step-summary"
          >
            <div className="text-xs font-semibold text-slate-700 mb-2">
              Mehrstufige Erfolgsrechnung (OR Art. 959b)
            </div>
            <dl className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
              <SummaryItem
                label="Betriebsergebnis (EBIT)"
                value={is_.operating_result}
                previous={is_.operating_result_previous}
                compare={compare}
                currency={is_.currency}
                testId="kpi-operating-result"
              />
              <SummaryItem
                label="Finanzergebnis netto"
                value={is_.financial_result_total}
                currency={is_.currency}
                testId="kpi-financial-result"
              />
              <SummaryItem
                label="Ergebnis vor Steuern"
                value={is_.result_before_taxes}
                previous={is_.result_before_taxes_previous}
                compare={compare}
                currency={is_.currency}
                testId="kpi-result-before-taxes"
              />
              <SummaryItem
                label="Direkte Steuern"
                value={is_.income_taxes_total}
                currency={is_.currency}
                testId="kpi-income-taxes"
              />
            </dl>
          </div>
        </Card>
      )}

      {notes && (
        <Card className="p-5 border border-slate-200 bg-white" data-testid="notes-card">
          <div className="flex items-baseline justify-between mb-3">
            <div>
              <h3 className="text-base font-semibold text-slate-900">Anhang zur Jahresrechnung</h3>
              <p className="text-xs text-slate-500">
                OR Art. 959c · Geschäftsjahr {notes.year}
              </p>
            </div>
            <span className="text-xs text-slate-500">{notes.currency}</span>
          </div>

          <div className="space-y-5">
            {/* 1. Grundsätze */}
            <section data-testid="notes-principles">
              <h4 className="text-sm font-semibold text-slate-800 mb-2">1. Allgemeine Grundsätze</h4>
              <ul className="text-xs text-slate-700 space-y-1 list-disc pl-5">
                {(notes.accounting_principles || []).map((p, i) => (
                  <li key={i}>{p}</li>
                ))}
              </ul>
            </section>

            {/* 2. Anlagespiegel */}
            <section data-testid="notes-asset-schedule">
              <h4 className="text-sm font-semibold text-slate-800 mb-2">2. Anlagespiegel</h4>
              {notes.asset_schedule.length === 0 ? (
                <p className="text-xs text-slate-500 italic">Keine Anlagen erfasst.</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="text-xs">Anlage</TableHead>
                      <TableHead className="text-right text-xs">BV 01.01.</TableHead>
                      <TableHead className="text-right text-xs">+ Zugang</TableHead>
                      <TableHead className="text-right text-xs">− Abgang</TableHead>
                      <TableHead className="text-right text-xs">− Abschr.</TableHead>
                      <TableHead className="text-right text-xs">BV 31.12.</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {notes.asset_schedule.map((grp) => (
                      <React.Fragment key={grp.account_id}>
                        <TableRow className="bg-slate-50">
                          <TableCell colSpan={6} className="font-semibold text-xs">
                            {grp.account_id} {grp.account_name || ""}
                          </TableCell>
                        </TableRow>
                        {grp.lines.map((ln) => (
                          <TableRow key={ln.asset_id} data-testid={`notes-asset-row-${ln.asset_id}`}>
                            <TableCell className="text-xs">
                              {ln.name}
                              {ln.is_addition && (
                                <span className="ml-1.5 text-[9px] uppercase tracking-wide text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-1 py-px">Zugang</span>
                              )}
                              {ln.is_disposal && (
                                <span className="ml-1.5 text-[9px] uppercase tracking-wide text-rose-700 bg-rose-50 border border-rose-200 rounded px-1 py-px">Abgang</span>
                              )}
                              <span className="ml-1.5 text-slate-400">({ln.useful_life_years} J.)</span>
                            </TableCell>
                            <TableCell className="text-right font-mono text-xs">{fmt(ln.book_value_opening || 0)}</TableCell>
                            <TableCell className="text-right font-mono text-xs text-emerald-700">
                              {ln.is_addition ? fmt(ln.acquisition_cost) : "—"}
                            </TableCell>
                            <TableCell className="text-right font-mono text-xs text-rose-700">
                              {ln.is_disposal ? fmt(ln.book_value_opening || 0) : "—"}
                            </TableCell>
                            <TableCell className="text-right font-mono text-xs">{fmt(ln.period_depreciation || 0)}</TableCell>
                            <TableCell className="text-right font-mono text-xs font-semibold">{fmt(ln.book_value)}</TableCell>
                          </TableRow>
                        ))}
                        <TableRow className="border-t border-slate-200">
                          <TableCell className="text-xs font-semibold">
                            Total {grp.account_name || grp.account_id}
                          </TableCell>
                          <TableCell className="text-right font-mono text-xs font-semibold">
                            {fmt(grp.total_book_value_opening || 0)}
                          </TableCell>
                          <TableCell className="text-right font-mono text-xs font-semibold text-emerald-700">
                            {fmt(grp.total_additions || 0)}
                          </TableCell>
                          <TableCell className="text-right font-mono text-xs font-semibold text-rose-700">
                            {fmt(grp.total_disposals || 0)}
                          </TableCell>
                          <TableCell className="text-right font-mono text-xs font-semibold">
                            {fmt(grp.total_period_depreciation || 0)}
                          </TableCell>
                          <TableCell className="text-right font-mono text-xs font-semibold">
                            {fmt(grp.total_book_value)}
                          </TableCell>
                        </TableRow>
                      </React.Fragment>
                    ))}
                    <TableRow className="bg-slate-900 text-white">
                      <TableCell>Total Anlagen</TableCell>
                      <TableCell className="text-right font-mono">{fmt(notes.asset_schedule_total_book_value_opening || 0)}</TableCell>
                      <TableCell className="text-right font-mono">{fmt(notes.asset_schedule_total_additions || 0)}</TableCell>
                      <TableCell className="text-right font-mono">{fmt(notes.asset_schedule_total_disposals || 0)}</TableCell>
                      <TableCell className="text-right font-mono">{fmt(notes.asset_schedule_total_period_depreciation || 0)}</TableCell>
                      <TableCell className="text-right font-mono">{fmt(notes.asset_schedule_total_book_value)}</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              )}
            </section>

            {/* 3. Eigenkapital-Bewegung */}
            <section data-testid="notes-equity-movement">
              <h4 className="text-sm font-semibold text-slate-800 mb-2">3. Eigenkapital-Bewegung</h4>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-xs">Position</TableHead>
                    <TableHead className="text-right text-xs">Anfang 01.01.</TableHead>
                    <TableHead className="text-right text-xs">Veränderung</TableHead>
                    <TableHead className="text-right text-xs">Ende 31.12.</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {notes.equity_movement.map((r, i) => (
                    <TableRow key={i} data-testid={`notes-equity-row-${i}`}>
                      <TableCell className="text-xs">{r.label}</TableCell>
                      <TableCell className="text-right font-mono text-xs">{fmt(r.opening_balance)}</TableCell>
                      <TableCell className={`text-right font-mono text-xs ${r.movement < 0 ? "text-rose-700" : r.movement > 0 ? "text-emerald-700" : ""}`}>
                        {fmt(r.movement)}
                      </TableCell>
                      <TableCell className="text-right font-mono text-xs">{fmt(r.closing_balance)}</TableCell>
                    </TableRow>
                  ))}
                  <TableRow className="bg-slate-900 text-white">
                    <TableCell>Total Eigenkapital</TableCell>
                    <TableCell className="text-right font-mono">{fmt(notes.equity_total_opening)}</TableCell>
                    <TableCell className="text-right font-mono">
                      {fmt((notes.equity_total_closing || 0) - (notes.equity_total_opening || 0))}
                    </TableCell>
                    <TableCell className="text-right font-mono">{fmt(notes.equity_total_closing)}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </section>

            {/* 4. Sonderangaben */}
            <section data-testid="notes-special">
              <h4 className="text-sm font-semibold text-slate-800 mb-2">4. Sonderangaben</h4>
              <ul className="text-xs text-slate-700 space-y-1 list-disc pl-5">
                <li data-testid="notes-pension">
                  Verbindlichkeiten gegenüber Vorsorgeeinrichtungen
                  {notes.pension_liabilities_account && (
                    <span className="text-slate-500"> (Konto {notes.pension_liabilities_account})</span>
                  )}
                  : <span className="font-mono font-semibold">{fmt(notes.pension_liabilities)} {notes.currency}</span>
                </li>
                <li data-testid="notes-fte">
                  Durchschnittliche Vollzeitstellen:{" "}
                  {notes.fte_average !== null && notes.fte_average !== undefined ? (
                    <span className="font-mono font-semibold">{notes.fte_average.toFixed(1)}</span>
                  ) : (
                    <span className="text-slate-400 italic">nicht erfasst</span>
                  )}
                </li>
              </ul>
            </section>
          </div>
        </Card>
      )}
    </div>
  );
}
